import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, effect, ElementRef, inject, input, InputSignal, PLATFORM_ID, signal, viewChild, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { GsFocusTrapDirective } from '../../directives/focus-trap.directive';
import { Color } from '../../models';
import { StyleService } from '../../services';

@Component({
  selector: 'gs-sidebar',
  imports: [CommonModule, GsFocusTrapDirective],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './gs-sidebar.component.html'
})
export class GsSidebarComponent {
  // Services
  protected readonly styleService = inject(StyleService);
  /** Whether the component runs in the browser; the server render never moves the element to the body (hydration) */
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  // Inputs & Models
  /** Should it be possible to close the sidebar via a click on the outside area */
  enableBackdrop: InputSignal<boolean> = input(true);
  /** Max width of the sidebar (in pixels) */
  sidebarWidth: InputSignal<number> = input<number>(380);
  /** Background color of the sidebar */
  backgroundColor: InputSignal<Color | undefined> = input<Color | undefined>(undefined);
  /** Accessible name for the sidebar dialog */
  ariaLabel: InputSignal<string | undefined> = input<string | undefined>(undefined);

  // Properties & Signals
  /** Is the sidebar currently open? */
  opened: WritableSignal<boolean> = signal(false);
  /** Has the sidebar been opened at least once? Used to prevent transition flash on initial render */
  protected hasBeenOpened: WritableSignal<boolean> = signal(false);

  // ViewChilds
  /** Wrapper around backdrop and panel, moved to the body as one so both keep their query container */
  private readonly sidebarHostRef = viewChild<ElementRef<HTMLDivElement>>('sidebarHostRef');

  constructor() {
    // Apply opened state changes to the sidebar element / append to body if first opened
    effect(() => {
      const value = this.opened();
      if (value && this.isBrowser) {
        const hostElement = this.sidebarHostRef();
        if (hostElement && hostElement.nativeElement.parentElement !== document.body) {
          document.body.appendChild(hostElement.nativeElement);
        }
      }
    });
  }

  // Methods
  /**
   * Toggles the opened state of the sidebar
   */
  public toggle(state?: boolean) {
    this.opened.update((currState) => (state != null ? state : !currState));
    if (this.opened() && !this.hasBeenOpened()) {
      this.hasBeenOpened.set(true);
    }
  }

  /**
   * Closes the sidebar
   */
  public dismiss() {
    this.opened.set(false);
  }
}
